import { useCallback, useEffect, useState } from 'react'
import { useAppSelector } from '@/store/hooks'

// 메뉴마다 따로 고르던 사업 필터를 쿠키에 저장해 화면을 옮겨도 같은 사업으로 필터한다.
// 워크스페이스별로 사업이 다르므로 저장한 워크스페이스와 다르면 무시한다.

const COOKIE_NAME = 'dot_selected_business'
const MAX_AGE_SECONDS = 60 * 60 * 24 * 30

interface StoredBusiness {
  workspaceId: string
  id: string
  name: string
}

const EMPTY = { id: '', name: '' }

const readCookie = (): StoredBusiness | null => {
  const raw = document.cookie
    .split('; ')
    .find((part) => part.startsWith(`${COOKIE_NAME}=`))
  if (!raw) return null
  try {
    const value = JSON.parse(
      decodeURIComponent(raw.slice(COOKIE_NAME.length + 1))
    )
    return value && typeof value.id === 'string'
      ? (value as StoredBusiness)
      : null
  } catch {
    return null
  }
}

const readForWorkspace = (workspaceId?: string) => {
  const stored = readCookie()
  return stored && workspaceId && stored.workspaceId === workspaceId
    ? { id: stored.id, name: stored.name }
    : EMPTY
}

const writeCookie = (
  workspaceId: string | undefined,
  id: string,
  name: string
) => {
  // '전체'(빈 값)를 고르면 저장값을 지운다
  if (!workspaceId || !id) {
    document.cookie = `${COOKIE_NAME}=; path=/; max-age=0; SameSite=Lax`
    return
  }
  const value = encodeURIComponent(JSON.stringify({ workspaceId, id, name }))
  document.cookie = `${COOKIE_NAME}=${value}; path=/; max-age=${MAX_AGE_SECONDS}; SameSite=Lax`
}

/** 쿠키에 저장되는 사업 필터. 빈 id 는 '전체' */
export function usePersistedBusinessFilter() {
  const workspaceId = useAppSelector(
    (state) => state.workspace.selectedWorkspace?.id
  )
  const [business, setBusinessState] = useState(() =>
    readForWorkspace(workspaceId)
  )

  // 워크스페이스를 바꾸면 그 워크스페이스에 저장된 사업으로
  useEffect(() => {
    setBusinessState(readForWorkspace(workspaceId))
  }, [workspaceId])

  const setBusiness = useCallback(
    (id: string, name: string) => {
      setBusinessState({ id, name })
      writeCookie(workspaceId, id, name)
    },
    [workspaceId]
  )

  return { businessId: business.id, businessName: business.name, setBusiness }
}
